import { ref } from 'vue'
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useCompanyStore } from '@/stores/company'

const hiddenTemplateIds = ref<string[]>([])
const isLoading = ref(false)

function getPreferencesDocRef() {
  const db = FirebaseProvider.getInstance().getFirestore()
  const auth = FirebaseProvider.getInstance().getAuth()
  const companyId = useCompanyStore().selectedCompanyId
    || localStorage.getItem('selectedCompanyId')
    || ''
  const userId = auth.currentUser?.uid || ''
  if (!companyId || !userId) return null
  return doc(db, `Companies/${companyId}/users/${userId}/preferences/emailTemplates`)
}

export function useTemplatePreferences() {
  async function fetchPreferences(): Promise<void> {
    const docRef = getPreferencesDocRef()
    if (!docRef) {
      hiddenTemplateIds.value = []
      return
    }

    isLoading.value = true
    try {
      const snap = await getDoc(docRef)
      hiddenTemplateIds.value = snap.exists()
        ? (snap.data().hiddenTemplateIds ?? [])
        : []
    } catch (err) {
      console.error('Error fetching template preferences:', err)
      hiddenTemplateIds.value = []
    } finally {
      isLoading.value = false
    }
  }

  async function hideTemplate(templateId: string): Promise<void> {
    const docRef = getPreferencesDocRef()
    if (!docRef) return

    hiddenTemplateIds.value = [...hiddenTemplateIds.value, templateId]
    try {
      const snap = await getDoc(docRef)
      if (snap.exists()) {
        await updateDoc(docRef, { hiddenTemplateIds: arrayUnion(templateId) })
      } else {
        await setDoc(docRef, { hiddenTemplateIds: [templateId] })
      }
    } catch (err) {
      console.error('Error hiding template:', err)
      hiddenTemplateIds.value = hiddenTemplateIds.value.filter((id) => id !== templateId)
    }
  }

  async function unhideTemplate(templateId: string): Promise<void> {
    const docRef = getPreferencesDocRef()
    if (!docRef) return

    hiddenTemplateIds.value = hiddenTemplateIds.value.filter((id) => id !== templateId)
    try {
      const snap = await getDoc(docRef)
      if (snap.exists()) {
        await updateDoc(docRef, { hiddenTemplateIds: arrayRemove(templateId) })
      }
    } catch (err) {
      console.error('Error unhiding template:', err)
      hiddenTemplateIds.value = [...hiddenTemplateIds.value, templateId]
    }
  }

  function isHidden(templateId: string): boolean {
    return hiddenTemplateIds.value.includes(templateId)
  }

  return {
    hiddenTemplateIds,
    isLoading,
    fetchPreferences,
    hideTemplate,
    unhideTemplate,
    isHidden,
  }
}
